import {useCallback, useEffect, useMemo, useState} from 'react';
import {useReportStore} from '../../useReportStore';
import {
    downloadReportVersionFromApi,
    listReportVersionsFromApi
} from '../../../../utils/api/reportVersions';
import {parseVersionFromSnapshotPath} from '../../previewPage/useReportDocumentSubscription';
import {getSnapshotCache, setSnapshotCache} from '../../../../utils/reportSnapshotCache';
import {useShallow} from 'zustand/react/shallow';
import {normalizePmDoc} from './generateInitialVersion/generationUtils';

function resolveParentReportId({parentProduct, meta, parentReportIdField}) {
    if (typeof parentProduct === 'string' && parentProduct.trim()) {
        return parentProduct;
    }
    return parentProduct?.uid
        || parentProduct?.id
        || meta?.parent_product?.uid
        || meta?.parent_product?.id
        || parentReportIdField
        || meta?.parentReportId
        || null;
}

/**
 * Hook to fetch parent report content for disclosure generation
 * Returns the parent report content and loading/error states
 */
export const useParentReportContent = () => {
    // Select only the specific fields we need to prevent unnecessary re-renders
    const {parentProduct, snapshotPath, meta, parentReportIdField} = useReportStore(
        useShallow(state => ({
            parentProduct: state.parent_product || state.meta?.parent_product,
            snapshotPath: state.snapshotPath,
            meta: state.meta,
            parentReportIdField: state.parentReportId
        }))
    );
    const parentReportId = resolveParentReportId({parentProduct, meta, parentReportIdField});
    // Read from parent_product.snapshotPath (new location), fallback to legacy snapshotPath field
    const parentSnapshotPath = parentProduct?.snapshotPath
        || meta?.parent_product?.snapshotPath
        || snapshotPath;
    const parentVersionFromPath = parseVersionFromSnapshotPath(parentSnapshotPath);
    const parentMeta = parentProduct?.meta || meta?.parent_product?.meta;

    const [parentContent, setParentContent] = useState(null);
    const [resolvedVersion, setResolvedVersion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchParentContent = useCallback(async () => {
        if (!parentReportId) {
            console.log('⚠️ Missing parent report id:', {parentReportId, parentSnapshotPath});
            setParentContent(null);
            setResolvedVersion(null);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let version = parentVersionFromPath;

            if (!version) {
                const versions = await listReportVersionsFromApi(parentReportId);
                if (!versions.length) {
                    throw new Error('No saved versions found for parent report');
                }
                version = String(versions[0].version);
                console.log('📥 Resolved parent report version from API:', {parentReportId, version});
            }

            console.log('📥 Fetching parent report content:', {parentReportId, version});

            const cached = getSnapshotCache(parentReportId, version);
            const result = cached || await downloadReportVersionFromApi(parentReportId, version);
            if (!cached) {
                setSnapshotCache(parentReportId, version, result);
            }
            // downloadReportVersionFromApi spreads pmJson onto the payload
            // ({type, content, version, reportId, ...}). Normalize so the modal
            // always receives a ProseMirror doc and extractBlocks can run.
            const normalized = normalizePmDoc(result);
            if (!normalized) {
                throw new Error('Parent report snapshot is not a readable document');
            }
            setParentContent(normalized);
            setResolvedVersion(String(result?.version ?? version));
            console.log('✅ Parent report content loaded successfully');
        } catch (err) {
            console.error('❌ Failed to load parent report content:', err);
            setError(err);
            setParentContent(null);
            setResolvedVersion(null);
        } finally {
            setLoading(false);
        }
    }, [parentReportId, parentVersionFromPath, parentSnapshotPath]);

    useEffect(() => {
        fetchParentContent();
    }, [fetchParentContent]);

    const parentVersion = resolvedVersion || parentVersionFromPath;

    return useMemo(() => ({
        parentContent,
        parentReportId,
        parentVersion,
        parentMeta,
        loading,
        error,
        refetch: fetchParentContent
    }), [parentContent, parentReportId, parentVersion, parentMeta, loading, error, fetchParentContent]);
};
